import { useMutation, useSuspenseQuery } from '@apollo/client/react'
import { useCallback, useMemo } from 'react'
import { useLocation } from 'wouter'

import {
  AdminAPI,
  getFragment,
  makeFragmentData,
  SharedAPI
} from '@guestgoodie/graphql'

export default function useCategory(
  layoutId: number,
  slug: string,
  withGoodie = false
) {
  const [update] = useMutation(AdminAPI.category.UPDATE)

  const { data } = useSuspenseQuery(AdminAPI.category.GET_BY_LAYOUT_AND_SLUG, {
    variables: { layoutId, slug, withLayout: true, withGoodie }
  })

  const category = useMemo(() => {
    return getFragment(
      SharedAPI.category.CATEGORY_FRAGMENT,
      data?.getCategoryByLayoutAndSlug
    )
  }, [data])

  const layout = useMemo(
    () => getFragment(SharedAPI.layout.LAYOUT_FRAGMENT, category?.layout),
    [category]
  )

  const [, navigate] = useLocation()

  const addGoodies = useCallback(() => {
    navigate(`/add-category-goodies/${layout?.version}/${slug}`)
  }, [navigate, layout, slug])

  const removeGoodies = useCallback(
    (ids: Set<number>) => {
      if (!category) return

      const categoryGoodies = category.categoryGoodies
        .toSorted(({ position: posA }, { position: posB }) => posA - posB)
        .filter(({ goodieId }) => !ids.has(goodieId))
        .map(({ goodieId }, position) => ({ goodieId, position }))

      update({
        variables: {
          input: {
            id: category.id,
            goodieIds: categoryGoodies.map(({ goodieId }) => goodieId)
          }
        },
        optimisticResponse: {
          updateCategory: makeFragmentData(
            {
              ...category,
              categoryGoodies
            },
            SharedAPI.category.CATEGORY_FRAGMENT
          )
        }
      })
    },
    [category, update]
  )

  const moveGoodies = useCallback(
    (ids: Set<number>, position: number) => {
      if (!category) return
      if (position < 0 || position >= category.categoryGoodies.length) return

      const sortedGoodies = category.categoryGoodies.toSorted(
        ({ position: a }, { position: b }) => a - b
      )

      const categoryGoodieMap = new Map(
        sortedGoodies.map(cg => [cg.goodieId, cg])
      )

      const newGoodieSeq = sortedGoodies
        .map(({ goodieId }) => goodieId)
        .filter(id => !ids.has(id))
        .toSpliced(position, 0, ...Array.from(ids))

      update({
        variables: {
          input: {
            id: category.id,
            goodieIds: newGoodieSeq
          },
          withGoodie,
          withLayout: true
        },
        optimisticResponse: {
          updateCategory: makeFragmentData(
            {
              ...category,
              categoryGoodies: newGoodieSeq
                .map(id => categoryGoodieMap.get(id))
                .filter(id => id != null)
                .map((cg, position) => ({ ...cg, position }))
            },
            SharedAPI.category.CATEGORY_FRAGMENT
          )
        }
      })
    },
    [category, update, withGoodie]
  )

  return {
    category,
    addGoodies,
    moveGoodies,
    removeGoodies
  }
}
